import { useQuery } from '@tanstack/react-query';
import { getDynamicSenderNamesQueryOptions } from '@/services/db/identity/queries';
import { isEmptyString } from '@/utils/string';
import { dynamicSenderUidKey, isDynamicSenderUid } from './dynamic-sender-uid';

const DYNAMIC_SENDER_LABEL = 'Dynamic sender';

export type SenderLabel = { label: string; isLoading: boolean };

type SenderLabelOptions = {
  workspaceId: number;
  sender: string;
  /** The sender's name from the workspace's identities, which leave deleted senders out. */
  name: string | undefined;
  isNameLoading: boolean;
};

/**
 * The label for a sender that a condition or picker refers to: its name when
 * it has one, and otherwise the sender as stored. A dynamic sender's uid is
 * never shown; one missing from the workspace's identities, such as a deleted
 * one, is named from the dynamic sender names lookup.
 */
export function useSenderLabel({ workspaceId, sender, name, isNameLoading }: SenderLabelOptions): SenderLabel {
  const isDynamic = isDynamicSenderUid(sender);
  const names = useQuery({
    ...getDynamicSenderNamesQueryOptions(workspaceId),
    enabled: workspaceId > 0 && isDynamic && !isNameLoading && name == null,
  });
  if (name != null) return { label: name, isLoading: false };
  if (!isDynamic) return { label: sender, isLoading: isNameLoading };
  return { label: lookedUpName(names.data, sender), isLoading: isNameLoading || names.isLoading };
}

function lookedUpName(names: Record<string, string> | undefined, sender: string): string {
  const name = names?.[dynamicSenderUidKey(sender)];
  return isEmptyString(name) ? DYNAMIC_SENDER_LABEL : name;
}
